/* eslint-disable react-hooks/incompatible-library */
import { useMemo, useState, type ReactNode } from 'react'
import {
    type ColumnDef,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    useReactTable,
    type RowSelectionState,
    type PaginationState,
} from '@tanstack/react-table'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { Checkbox } from '@/components/ui/checkbox'
import { DataTablePagination } from './DataTablePagination'
import { DataTableToolbar } from './DataTableToolbar'
import { DataTableActions } from './DataTableActions'

interface DataTableProps<TData, TValue> {
    columns: ColumnDef<TData, TValue>[]
    data: TData[]
    searchPlaceholder?: string
    onSearch?: (value: string) => void
    onFilter?: () => void
    enableRowSelection?: boolean
    /** When true, search is debounced and sent to the API via onSearch. When false (default), filtering is client-side only. */
    debounceSearch?: boolean
    onViewRow?: (row: TData) => void
    onDownloadReceipt?: (rows: TData[]) => void
    downloadLabel?: string
    viewLabel?: string
    customActions?: Array<{
        label: string | ((rows: TData[]) => string)
        onClick: (rows: TData[]) => void
        icon?: ReactNode | ((rows: TData[]) => ReactNode)
        showForSingle?: boolean
        showForMultiple?: boolean
    }>
    showViewButton?: boolean
    toolbarActions?: ReactNode
}

export function DataTable<TData, TValue>({
    columns: userColumns,
    data,
    searchPlaceholder = 'Search...',
    onSearch,
    onFilter,
    enableRowSelection = true,
    debounceSearch = false,
    onViewRow,
    onDownloadReceipt,
    downloadLabel = 'Download receipt',
    viewLabel,
    customActions = [],
    showViewButton = true,
    toolbarActions,
}: DataTableProps<TData, TValue>) {
    const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
    const [pagination, setPagination] = useState<PaginationState>({
        pageIndex: 0,
        pageSize: 10,
    })
    const [globalFilter, setGlobalFilter] = useState('')

    // Add selection column if enabled
    const columns: ColumnDef<TData, TValue>[] = useMemo(() => {
        if (!enableRowSelection) return userColumns

        return [
            {
                id: 'select',
                header: ({ table }) => (
                    <Checkbox
                        checked={
                            table.getIsAllPageRowsSelected()
                                ? true
                                : table.getIsSomePageRowsSelected()
                                  ? 'indeterminate'
                                  : false
                        }
                        onCheckedChange={value =>
                            table.toggleAllPageRowsSelected(value === true)
                        }
                        aria-label="Select all rows on page"
                    />
                ),
                cell: ({ row }) => (
                    <Checkbox
                        checked={row.getIsSelected()}
                        onCheckedChange={value =>
                            row.toggleSelected(value === true)
                        }
                        aria-label="Select row"
                        onClick={event => event.stopPropagation()}
                    />
                ),
                enableSorting: false,
                enableHiding: false,
            },
            ...userColumns,
        ]
    }, [enableRowSelection, userColumns])

    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        onRowSelectionChange: setRowSelection,
        onPaginationChange: setPagination,
        onGlobalFilterChange: setGlobalFilter,
        enableRowSelection,
        state: {
            rowSelection,
            pagination,
            globalFilter,
        },
    })

    const selectedRowsCount = table.getFilteredSelectedRowModel().rows.length
    const selectedRows = table
        .getFilteredSelectedRowModel()
        .rows.map(row => row.original)

    const customActionsForSelection = useMemo(
        () =>
            customActions
                .filter(action => {
                    if (selectedRowsCount === 1 && action.showForSingle)
                        return true
                    if (selectedRowsCount > 1 && action.showForMultiple)
                        return true
                    if (!action.showForSingle && !action.showForMultiple)
                        return true
                    return false
                })
                .map(action => {
                    const label =
                        typeof action.label === 'function'
                            ? action.label(selectedRows)
                            : action.label
                    const icon =
                        typeof action.icon === 'function'
                            ? action.icon(selectedRows)
                            : action.icon
                    return {
                        ...action,
                        label,
                        icon,
                        onClick: () => action.onClick(selectedRows),
                    }
                }),
        [customActions, selectedRows, selectedRowsCount]
    )

    return (
        <div className="space-y-4">
            {/* Show toolbar when no rows selected */}
            {selectedRowsCount === 0 && (
                <DataTableToolbar
                    searchPlaceholder={searchPlaceholder}
                    onSearch={value => {
                        if (debounceSearch) {
                            // API-driven: don't filter client-side, let parent handle it
                            onSearch?.(value)
                        } else {
                            // Client-side: filter the local data
                            setGlobalFilter(value)
                            onSearch?.(value)
                        }
                    }}
                    onFilter={onFilter}
                    actions={toolbarActions}
                    debounceSearch={debounceSearch}
                />
            )}

            {/* Table */}
            <div className="overflow-hidden rounded-md border">
                <Table>
                    <TableHeader className="overflow-hidden bg-[#F8FAFC]">
                        {selectedRowsCount > 0 ? (
                            <TableRow className="hover:bg-transparent">
                                <TableHead
                                    colSpan={
                                        table.getVisibleLeafColumns().length
                                    }
                                    className="h-12 bg-[#F8FAFC] px-2"
                                >
                                    <DataTableActions
                                        selectedCount={selectedRowsCount}
                                        onViewRow={
                                            selectedRowsCount === 1 && onViewRow
                                                ? () =>
                                                      onViewRow(selectedRows[0])
                                                : undefined
                                        }
                                        onDownloadReceipt={
                                            onDownloadReceipt
                                                ? () =>
                                                      onDownloadReceipt(
                                                          selectedRows
                                                      )
                                                : undefined
                                        }
                                        downloadLabel={downloadLabel}
                                        customActions={
                                            customActionsForSelection
                                        }
                                        onClearSelection={() =>
                                            table.resetRowSelection()
                                        }
                                        viewLabel={viewLabel}
                                        selectionState={
                                            table.getIsAllPageRowsSelected()
                                                ? true
                                                : table.getIsSomePageRowsSelected()
                                                  ? 'indeterminate'
                                                  : false
                                        }
                                        onToggleAll={value =>
                                            table.toggleAllPageRowsSelected(
                                                value
                                            )
                                        }
                                        showViewButton={showViewButton}
                                    />
                                </TableHead>
                            </TableRow>
                        ) : (
                            table.getHeaderGroups().map(headerGroup => (
                                <TableRow
                                    key={headerGroup.id}
                                    className="hover:bg-transparent"
                                >
                                    {headerGroup.headers.map(header => (
                                        <TableHead key={header.id}>
                                            {header.isPlaceholder
                                                ? null
                                                : flexRender(
                                                      header.column.columnDef
                                                          .header,
                                                      header.getContext()
                                                  )}
                                        </TableHead>
                                    ))}
                                </TableRow>
                            ))
                        )}
                    </TableHeader>
                    <TableBody>
                        {table.getRowModel().rows?.length ? (
                            table.getRowModel().rows.map(row => (
                                <TableRow
                                    key={row.id}
                                    data-state={
                                        row.getIsSelected() && 'selected'
                                    }
                                >
                                    {row.getVisibleCells().map(cell => (
                                        <TableCell key={cell.id}>
                                            {flexRender(
                                                cell.column.columnDef.cell,
                                                cell.getContext()
                                            )}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell
                                    colSpan={columns.length}
                                    className="h-24 text-center"
                                >
                                    No results.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>

            {/* Pagination */}
            <DataTablePagination table={table} />
        </div>
    )
}
